import React, { useCallback, useState } from 'react';
import {
  ConnectEventDetail,
  ConnectionResult,
} from '../../Services/IntegrationService';
import {
  InstallableIntegrationSchema,
  IntegrationAuthSchema,
  InstallableUserIntegrationSchema,
  IntegrationSchema,
} from '../../definitions/schemas';
import IntegrationErrorDialog, {
  ErrorMessages,
} from './IntegrationErrorDialog';
import { IntegrationProvider } from '../../enums/Integrations/IntegrationProvider';
import { useShowConfirm } from '../../lib/hook/useShowConfirm';
import IntegrationsInterface from '../../graphql/Integrations/IntegrationsInterface';
import UserIntegrationsInterface from '../../graphql/Integrations/UserIntegrationsInterface';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import useResource from '../../lib/hook/useResource';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';
import { UserAuthTmpFeatureConfigFlagEnum } from '../../lib/features/FeatureFlag';
import { IntegrationOwnerType } from '../../enums/Integrations/IntegrationType';
import { canDo } from '../../Services/GuardPermissionsService';
import RolesEnum from '../../enums/Roles';

type ConnectionSuccessCallback = (connection: IntegrationAuthSchema) => void;

interface UninstallIntegrationOptions {
  beforeSignOut?: () => void;
  connectionId: string;
  onError?: (error: string) => void;
  onSuccess?: () => void;
  providerName: string;
}

interface IIntegrationsContext {
  createIntegrationInstallHandler: (
    onSuccess?: ConnectionSuccessCallback,
    onError?: () => void
  ) => (e: any) => void;
  installableIntegrations: InstallableIntegrationSchema[];
  installableIntegrationsReady: boolean;
  installableUserIntegrations: InstallableUserIntegrationSchema[];
  installableUserIntegrationsReady: boolean;
  installedIntegrations: IntegrationSchema[];
  installedIntegrationsReady: boolean;
  installedUserIntegrations: IntegrationSchema[];
  installedUserIntegrationsReady: boolean;
  renderErrorDialog: () => JSX.Element;
  uninstallIntegration: (options: UninstallIntegrationOptions) => void;
  uninstallUserIntegration: (id: string) => Promise<void>;
}

export const IntegrationsContext: React.Context<IIntegrationsContext> =
  React.createContext<IIntegrationsContext>({
    createIntegrationInstallHandler: () => () => {},
    installableIntegrations: [],
    installableIntegrationsReady: false,
    installableUserIntegrations: [],
    installableUserIntegrationsReady: false,
    installedIntegrations: [],
    installedIntegrationsReady: false,
    installedUserIntegrations: [],
    installedUserIntegrationsReady: false,
    renderErrorDialog: () => <></>,
    uninstallIntegration: () => {},
    uninstallUserIntegration: () => Promise.resolve(),
  });

type Props = React.PropsWithChildren<unknown>;

const IntegrationsContextProvider: React.FC<Props> = (props: Props) => {
  const showConfirm = useShowConfirm();

  const shouldLoadUserIntegrationLists =
    useFeatureFlag(
      UserAuthTmpFeatureConfigFlagEnum.RELEASE_CONTROL_DSR_SFDC_USER_SCOPING,
      true
    ) && canDo(RolesEnum.SALES);

  const [integrationsLastUpdate, setIntegrationsLastUpdate] = useState(0);
  const [openConsentDialog, setOpenConsentDialog] = useState(false);
  const [connectionErrorMessage, setConnectionErrorMessage] =
    useState<string>();

  const [installableIntegrations, installableIntegrationsReady] = useResource(
    () => new IntegrationsInterface().installableIntegrations(),
    [],
    [integrationsLastUpdate]
  );

  const [installedIntegrations, installedIntegrationsReady] = useResource(
    () => loadInstalledIntegrations(),
    [],
    [integrationsLastUpdate]
  );

  const loadInstalledIntegrations = async (
    ownerType?: IntegrationOwnerType
  ): Promise<IntegrationSchema[]> => {
    const integrations = await new IntegrationsInterface().search({
      ownerType,
    });
    return integrations ?? [];
  };

  const [installableUserIntegrations, installableUserIntegrationsReady] =
    useResource(
      () =>
        shouldLoadUserIntegrationLists
          ? new UserIntegrationsInterface().installableUserIntegrations()
          : Promise.resolve([]),
      [],
      [integrationsLastUpdate, shouldLoadUserIntegrationLists]
    );

  const [installedUserIntegrations, installedUserIntegrationsReady] =
    useResource(
      () =>
        shouldLoadUserIntegrationLists
          ? loadInstalledIntegrations(IntegrationOwnerType.USER)
          : Promise.resolve([]),
      [],
      [integrationsLastUpdate, shouldLoadUserIntegrationLists]
    );

  const uninstallUserIntegration = useCallback(async (id: string) => {
    await new UserIntegrationsInterface().disconnectUserCrm(id);
    setIntegrationsLastUpdate(Date.now());
  }, []);

  const createIntegrationInstallHandler = (
    onSuccess?: (auth: IntegrationAuthSchema) => void,
    onError?: () => void
  ) => {
    const onConnectError = (errorMessage?: string) => {
      if (errorMessage && errorMessage in ErrorMessages) {
        setConnectionErrorMessage(errorMessage);
        setOpenConsentDialog(true);
      } else {
        SnackbarStore.show(`${errorMessage}`);
      }

      if (onError) {
        onError();
      }
    };

    return (e: CustomEvent<ConnectEventDetail>) => {
      const { auth, result, message } = e.detail;

      switch (result) {
        case ConnectionResult.Error: {
          onConnectError(message);
          break;
        }
        case ConnectionResult.Success: {
          if (onSuccess) {
            onSuccess(auth);
          }

          setIntegrationsLastUpdate(Date.now());
          break;
        }
        default:
          onConnectError('Connection error');
      }
    };
  };

  const uninstallIntegration = async (options: UninstallIntegrationOptions) => {
    const { beforeSignOut, connectionId, onError, onSuccess, providerName } =
      options;

    const name =
      providerName === IntegrationProvider.DOCUSIGN ? 'DocuSign' : providerName;

    showConfirm({
      title: `Disconnect from ${name}`,
      description: `Are you sure you want to disconnect from your ${name} integration?`,
      confirmationText: 'Disconnect',
      useErrorColor: true,
    })
      .then(async () => {
        if (beforeSignOut) {
          beforeSignOut();
        }

        try {
          await new IntegrationsInterface().uninstall({ id: connectionId });
          // Notify a successful disconnect
          if (onSuccess) {
            onSuccess();
          }
          // Reload providers
          setIntegrationsLastUpdate(Date.now());
        } catch (e) {
          const error = e as string;

          if (onError) {
            onError(error);
          }

          SnackbarStore.show(error);
        }
      })
      .catch(() => {
        /* do nothing */
      });
  };

  const closeConsentDialog = () => {
    setOpenConsentDialog(false);
  };

  const renderErrorDialog = () => (
    <IntegrationErrorDialog
      errorMessage={connectionErrorMessage}
      onClose={closeConsentDialog}
      open={openConsentDialog}
    />
  );

  const context = {
    createIntegrationInstallHandler,
    installableIntegrations,
    installableIntegrationsReady,
    installableUserIntegrations,
    installableUserIntegrationsReady,
    installedIntegrations,
    installedIntegrationsReady,
    installedUserIntegrations,
    installedUserIntegrationsReady,
    renderErrorDialog,
    uninstallIntegration,
    uninstallUserIntegration,
  };

  return (
    <IntegrationsContext.Provider value={context}>
      {props.children}
    </IntegrationsContext.Provider>
  );
};

export default IntegrationsContextProvider;
